import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAgentAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, X, RefreshCw, KeyRound, CheckCircle2, Hash, User2, MapPin, Phone, Wallet, AlertCircle, Eye, EyeOff, PartyPopper, Receipt, ArrowDownToLine, Sparkles } from "lucide-react";

const fmt = (n) => `৳${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function PendingWithdrawals() {
  const { refresh } = useAgentAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveModal, setApproveModal] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [successModal, setSuccessModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agent/withdrawals?status=pending&limit=200");
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onApproved = (payload) => {
    setApproveModal(null);
    if (payload) setSuccessModal(payload);
    refresh();
    load();
  };
  const onRejected = () => {
    setRejectModal(null);
    load();
  };

  return (
    <div data-testid="agent-pending-page">
      <style>{`
        @keyframes pw-rise { 0% { opacity: 0; transform: translateY(8px); } 100% { opacity: 1; transform: none; } }
        .pw-rise { animation: pw-rise .4s cubic-bezier(.2,.8,.2,1) both; }
        @keyframes pw-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(245,158,11,0.4); } 50% { box-shadow: 0 0 0 6px rgba(245,158,11,0); } }
        .pw-pulse { animation: pw-pulse 2s ease-in-out infinite; }
      `}</style>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Pending Withdrawals</h1>
          <p className="text-sm text-white/60">Click <b className="text-emerald-300">Approve</b> on any request — you'll be asked for the user's 6-digit code to confirm.</p>
        </div>
        <button onClick={load} className="h-9 px-3 inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 rounded-md text-[13px] border border-white/10" data-testid="pending-refresh"><RefreshCw size={14} /> Refresh</button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <StatCard label="Pending count" value={items.length} icon={Hash} color="amber" />
        <StatCard label="Pending total" value={fmt(items.reduce((s, x) => s + Number(x.amount || 0), 0))} icon={Wallet} color="emerald" mono />
        <StatCard label="Oldest pending" value={items.length ? new Date(items[items.length - 1].created_at).toLocaleDateString([], { month: "short", day: "numeric" }) : "—"} icon={AlertCircle} color="violet" />
      </div>

      {/* List */}
      <div className="bg-[#0e1525]/80 border border-white/8 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2 text-[12px] text-white/60 font-bold uppercase tracking-[1.5px]">
          <Hash size={13} /> {items.length} pending request{items.length !== 1 ? "s" : ""}
        </div>
        {loading ? (
          <div className="h-32 flex items-center justify-center"><div className="h-7 w-7 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-14 text-white/40 text-[13px]">
            <div className="h-14 w-14 rounded-2xl bg-emerald-500/10 mx-auto grid place-items-center mb-3 border border-emerald-500/20">
              <CheckCircle2 className="text-emerald-400/70" size={24} />
            </div>
            <div className="font-bold text-white/70 mb-0.5">No pending withdrawals</div>
            <div className="text-white/40">All caught up — new requests will appear here automatically.</div>
          </div>
        ) : (
          <ul className="divide-y divide-white/5" data-testid="pending-list">
            {items.map((it, i) => (
              <li
                key={it.id}
                className="px-4 sm:px-5 py-4 hover:bg-white/[0.02] transition pw-rise"
                style={{ animationDelay: `${i * 40}ms` }}
                data-testid={`pending-item-${it.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  {/* User block */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="h-11 w-11 rounded-xl grid place-items-center shrink-0 font-black text-[15px]" style={{ background: "linear-gradient(135deg,#f59e0b,#f59e0bcc)", color: "#1a1208", boxShadow: "0 8px 18px -10px rgba(245,158,11,0.6)" }}>
                      {(it.player_name || it.username || "U").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-bold text-white text-[14px] truncate">{it.player_name}</span>
                        <span className="font-mono text-[10px] text-amber-300/85 bg-amber-500/10 border border-amber-500/25 px-1.5 py-0.5 rounded">@{it.username}</span>
                        <span className="font-mono text-[10px] text-white/45 bg-white/[0.04] border border-white/10 px-1.5 py-0.5 rounded">ID {it.player_id}</span>
                      </div>
                      <div className="text-[11px] text-white/45 mt-0.5 flex items-center gap-1.5">
                        <span>{new Date(it.created_at).toLocaleString()}</span>
                        {it.account_info && <><span className="text-white/20">·</span><span className="inline-flex items-center gap-1 truncate"><MapPin size={10} className="shrink-0" /> {it.account_info}</span></>}
                      </div>
                    </div>
                  </div>

                  {/* Amount */}
                  <div className="sm:text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-[1.5px] text-white/40 font-bold">Amount</div>
                    <div className="font-mono text-[18px] sm:text-[20px] font-extrabold text-emerald-300 tabular-nums" style={{ textShadow: "0 0 16px rgba(33,196,110,0.25)" }}>{fmt(it.amount)}</div>
                  </div>

                  {/* Pending badge with pulse */}
                  <div className="shrink-0 sm:ml-2">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10.5px] font-bold uppercase tracking-wider pw-pulse">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                      Pending
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 shrink-0 sm:ml-1">
                    <button
                      onClick={() => setApproveModal(it)}
                      className="h-9 px-4 bg-emerald-500 hover:bg-emerald-400 text-[#06140b] rounded-lg text-[12px] font-extrabold inline-flex items-center gap-1.5 transition active:scale-[0.97]"
                      style={{ boxShadow: "0 10px 22px -10px rgba(33,196,110,0.65)" }}
                      data-testid={`pending-approve-${it.id}`}
                    >
                      <CheckCircle2 size={14} strokeWidth={2.5} /> Approve
                    </button>
                    <button
                      onClick={() => setRejectModal(it)}
                      className="h-9 px-3 bg-rose-500/12 hover:bg-rose-500/20 text-rose-300 border border-rose-500/30 rounded-lg text-[12px] font-bold inline-flex items-center gap-1.5 transition active:scale-[0.97]"
                      data-testid={`pending-reject-${it.id}`}
                    >
                      <X size={14} strokeWidth={2.5} /> Reject
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {approveModal && <ApproveModal item={approveModal} onClose={() => setApproveModal(null)} onApproved={onApproved} />}
      {rejectModal && <RejectModal item={rejectModal} onClose={() => setRejectModal(null)} onRejected={onRejected} />}
      {successModal && <WithdrawReceivedModal data={successModal} onClose={() => setSuccessModal(null)} />}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, mono }) {
  const palette = {
    amber:   { bg: "rgba(245,158,11,0.10)",  border: "rgba(245,158,11,0.30)",  text: "text-amber-300" },
    emerald: { bg: "rgba(33,196,110,0.10)",  border: "rgba(33,196,110,0.30)",  text: "text-emerald-300" },
    violet:  { bg: "rgba(168,85,247,0.10)",  border: "rgba(168,85,247,0.30)",  text: "text-violet-300" },
  }[color] || {};
  return (
    <div className="rounded-xl p-3.5 border" style={{ background: palette.bg, borderColor: palette.border }}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={14} className={palette.text} />
        <div className={`text-[10px] uppercase tracking-[1.5px] font-bold ${palette.text}`}>{label}</div>
      </div>
      <div className={`text-xl font-extrabold ${mono ? "tabular-nums" : ""}`}>{value}</div>
    </div>
  );
}

// ============================================================
// Approve Modal — asks for the 6-digit code
// ============================================================
function ApproveModal({ item, onClose, onApproved }) {
  const [code, setCode] = useState("");
  const [showCode, setShowCode] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // 6 individual digit boxes
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const refs = [useState(null), useState(null), useState(null), useState(null), useState(null), useState(null)];

  useEffect(() => { setCode(digits.join("")); }, [digits]);

  const setDigit = (idx, v) => {
    const nv = (v || "").replace(/\D/g, "").slice(-1);
    setDigits((arr) => {
      const next = [...arr];
      next[idx] = nv;
      return next;
    });
    if (nv && idx < 5) {
      const el = document.querySelector(`[data-approve-digit-idx="${idx + 1}"]`);
      if (el) el.focus();
    }
  };

  const onKey = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      const el = document.querySelector(`[data-approve-digit-idx="${idx - 1}"]`);
      if (el) el.focus();
    }
  };

  const onPaste = (e) => {
    const t = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!t) return;
    e.preventDefault();
    setDigits(t.padEnd(6, "").split("").slice(0, 6).map((c) => (c.trim() ? c : "")));
    const last = Math.min(t.length, 6);
    const el = document.querySelector(`[data-approve-digit-idx="${last - 1}"]`);
    if (el) el.focus();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) { setErrorMsg("Enter the full 6-digit code"); return; }
    setSubmitting(true);
    setErrorMsg("");
    try {
      const { data } = await api.post(`/agent/withdrawals/${item.id}/approve`, { code });
      toast.success(`Approved ${fmt(data.amount)} — credited to your balance`);
      onApproved({ ...data, item });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Approval failed";
      setErrorMsg(typeof msg === "string" ? msg : "Approval failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 pw-rise" onClick={onClose}>
      <div className="bg-[#0e1525] border border-emerald-500/30 rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="approve-modal" style={{ boxShadow: "0 30px 80px -20px rgba(33,196,110,0.45)" }}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-white/5 bg-gradient-to-br from-emerald-500/10 to-transparent">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ background: "linear-gradient(135deg,#21c46e,#19a55c)", boxShadow: "0 10px 22px -10px rgba(33,196,110,0.6)" }}>
              <KeyRound size={18} className="text-[#06140b]" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-extrabold text-white tracking-tight">Approve Withdrawal</h3>
              <p className="text-[11.5px] text-white/55">Ask the user for their 6-digit code, then enter it below.</p>
            </div>
          </div>
        </div>

        {/* User info */}
        <div className="px-5 py-3 border-b border-white/5 bg-white/[0.02]">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg grid place-items-center shrink-0 font-black text-[13px]" style={{ background: "linear-gradient(135deg,#f59e0b,#f59e0bcc)", color: "#1a1208" }}>
              {(item.player_name || item.username || "U").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-white font-bold text-[13px] truncate">{item.player_name}</span>
                <span className="font-mono text-[10px] text-amber-300/85 bg-amber-500/10 border border-amber-500/25 px-1.5 py-0.5 rounded">@{item.username}</span>
              </div>
              <div className="text-[11px] text-white/45 mt-0.5 inline-flex items-center gap-1">
                <User2 size={10} /> ID {item.player_id}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-[1.5px] text-white/40 font-bold">Amount</div>
              <div className="font-mono text-lg font-extrabold text-emerald-300 tabular-nums" data-testid="approve-modal-amount">{fmt(item.amount)}</div>
            </div>
          </div>
        </div>

        {/* Code input form */}
        <form onSubmit={submit} className="px-5 py-5">
          <div className="flex items-center justify-between mb-2.5">
            <label className="text-[11px] uppercase tracking-[1.6px] text-white/55 font-bold inline-flex items-center gap-1.5">
              <ShieldCheck size={12} className="text-emerald-400" /> Withdrawal Code
            </label>
            <button type="button" onClick={() => setShowCode((s) => !s)} className="text-[11px] text-white/45 hover:text-white/80 inline-flex items-center gap-1" data-testid="approve-toggle-mask">
              {showCode ? <Eye size={12} /> : <EyeOff size={12} />}
              {showCode ? "Visible" : "Hidden"}
            </button>
          </div>

          {/* 6-digit boxes */}
          <div className="flex items-center gap-1.5 sm:gap-2 justify-between" onPaste={onPaste}>
            {digits.map((d, idx) => (
              <input
                key={idx}
                data-approve-digit-idx={idx}
                data-testid={`approve-digit-${idx}`}
                type={showCode ? "text" : "password"}
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={(e) => setDigit(idx, e.target.value)}
                onKeyDown={(e) => onKey(idx, e)}
                autoFocus={idx === 0}
                className="w-12 h-14 sm:w-14 sm:h-16 rounded-xl bg-[#070b12] border-2 border-white/12 text-center text-2xl sm:text-3xl font-mono font-black text-emerald-300 tabular-nums focus:border-emerald-500/70 focus:outline-none focus:ring-2 focus:ring-emerald-500/15 transition tracking-wide"
                style={d ? { boxShadow: "inset 0 0 0 1px rgba(33,196,110,0.45), 0 0 18px -6px rgba(33,196,110,0.45)" } : undefined}
              />
            ))}
          </div>

          {errorMsg && (
            <div className="mt-3 rounded-lg px-3 py-2 bg-rose-500/10 border border-rose-500/30 text-rose-300 text-[11.5px] flex items-start gap-1.5" data-testid="approve-error">
              <AlertCircle size={13} className="shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          <div className="rounded-lg px-3 py-2 bg-emerald-500/[0.06] border border-emerald-500/20 text-[11px] text-emerald-200/90 mt-3 flex items-start gap-1.5">
            <ShieldCheck size={13} className="mt-0.5 shrink-0 text-emerald-300" />
            <span>Once approved, ৳{Number(item.amount).toLocaleString()} will be credited to your wallet (plus your withdraw commission, if any).</span>
          </div>

          <div className="flex items-center gap-2 mt-5">
            <button type="button" onClick={onClose} className="flex-1 h-11 border border-white/10 hover:bg-white/5 text-white/75 rounded-lg text-[13px] font-bold transition" data-testid="approve-cancel">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || code.length !== 6}
              className="flex-1 h-11 rounded-lg text-[#06140b] font-extrabold text-[13px] inline-flex items-center justify-center gap-1.5 transition disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
              style={{ background: "linear-gradient(135deg,#21c46e 0%,#19a55c 100%)", boxShadow: "0 14px 30px -12px rgba(33,196,110,0.65)" }}
              data-testid="approve-submit"
            >
              {submitting ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle2 size={14} strokeWidth={2.5} />}
              {submitting ? "Approving…" : "Verify & Approve"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// Reject Modal
// ============================================================
function RejectModal({ item, onClose, onRejected }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/agent/withdrawals/${item.id}/reject`, { reason });
      toast.success("Withdrawal rejected — user has been refunded");
      onRejected();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 pw-rise" onClick={onClose}>
      <div className="bg-[#0e1525] border border-rose-500/30 rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="reject-modal" style={{ boxShadow: "0 30px 80px -20px rgba(244,63,94,0.4)" }}>
        <div className="px-5 py-4 border-b border-white/5 bg-gradient-to-br from-rose-500/10 to-transparent">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0 bg-rose-500/20 border border-rose-500/40">
              <X size={18} className="text-rose-300" strokeWidth={2.5} />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-white tracking-tight">Reject Withdrawal</h3>
              <p className="text-[11.5px] text-white/55">User <span className="font-mono text-amber-300">@{item.username}</span> will be refunded {fmt(item.amount)} immediately.</p>
            </div>
          </div>
        </div>

        <div className="px-5 py-5">
          <label className="block text-[11px] uppercase tracking-[1.6px] text-white/55 mb-1.5 font-bold">Reason (visible to user)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full h-24 px-3 py-2.5 bg-[#070b12] border border-white/10 rounded-lg text-sm focus:border-rose-500/60 focus:outline-none focus:ring-2 focus:ring-rose-500/15 transition resize-none"
            placeholder="e.g. Cash unavailable, address unreachable, etc."
            data-testid="reject-reason"
          />
          <div className="flex items-center gap-2 mt-4">
            <button onClick={onClose} className="flex-1 h-11 border border-white/10 hover:bg-white/5 text-white/75 rounded-lg text-[13px] font-bold transition" data-testid="reject-cancel">Cancel</button>
            <button
              disabled={busy}
              onClick={submit}
              className="flex-1 h-11 bg-rose-500 hover:bg-rose-600 text-white rounded-lg text-[13px] font-extrabold disabled:opacity-50 inline-flex items-center justify-center gap-1.5 active:scale-[0.98] transition"
              style={{ boxShadow: "0 14px 30px -12px rgba(244,63,94,0.55)" }}
              data-testid="reject-submit"
            >
              {busy ? <RefreshCw size={14} className="animate-spin" /> : <X size={14} strokeWidth={2.5} />}
              {busy ? "Rejecting…" : "Reject & refund"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ============================================================
 * Withdraw Received Successful — celebratory modal
 * Shown after agent approves a player's withdrawal request.
 * The agent has just RECEIVED the cash from the user, so we
 * frame the popup as "Withdraw Received Successful" and show
 * the credited amount + commission + new balance.
 * ============================================================ */
function WithdrawReceivedModal({ data, onClose }) {
  const item = data.item || {};
  const amount = data.amount ?? item.amount ?? 0;
  const commission = data.commission_amount ?? 0;
  const commissionPct = data.commission_pct ?? 0;
  const newBalance = data.agent_balance ?? 0;
  const totalCredited = Number(amount) + Number(commission);
  const username = item.username || "";
  const playerName = item.player_name || username;
  const playerId = item.player_id || "";
  const refId = (data.withdrawal_id || "").slice(0, 8).toUpperCase();
  const when = new Date().toLocaleString();

  return (
    <div
      data-testid="withdraw-received-modal"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ animation: "wr-fade-in 220ms ease-out forwards" }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        onClick={onClose}
        style={{
          background: "radial-gradient(circle at 50% 40%, rgba(16,185,129,0.25) 0%, rgba(2,6,12,0.85) 55%, rgba(0,0,0,0.92) 100%)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
      />

      {/* Confetti dots */}
      <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(20)].map((_, i) => {
          const colors = ["#34d399", "#fbbf24", "#a7f3d0", "#fde68a", "#10b981", "#fcd34d"];
          const left = (i * 47) % 100;
          const delay = (i * 73) % 1500;
          const dur = 1800 + ((i * 131) % 1400);
          const size = 6 + (i % 4) * 2;
          const color = colors[i % colors.length];
          return (
            <span
              key={i}
              style={{
                position: "absolute",
                left: `${left}%`,
                top: "-20px",
                width: size,
                height: size,
                background: color,
                borderRadius: i % 2 ? "50%" : "2px",
                opacity: 0,
                animation: `wr-confetti ${dur}ms ${delay}ms ease-in forwards`,
                boxShadow: `0 0 ${size * 2}px ${color}`,
              }}
            />
          );
        })}
      </div>

      {/* Card */}
      <div
        data-testid="withdraw-received-card"
        className="relative w-full max-w-md rounded-3xl overflow-hidden border border-emerald-400/30"
        style={{
          background: "linear-gradient(160deg, rgba(13,32,24,0.96) 0%, rgba(8,14,22,0.98) 55%, rgba(28,18,8,0.96) 100%)",
          boxShadow: "0 30px 80px -20px rgba(16,185,129,0.45), 0 0 0 1px rgba(255,255,255,0.04) inset",
          animation: "wr-pop-in 360ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
        }}
      >
        <div aria-hidden className="absolute -top-32 -right-24 h-64 w-64 rounded-full" style={{ background: "rgba(16,185,129,0.32)", filter: "blur(70px)" }} />
        <div aria-hidden className="absolute -bottom-32 -left-24 h-64 w-64 rounded-full" style={{ background: "rgba(245,158,11,0.20)", filter: "blur(80px)" }} />
        <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-300/70 to-transparent" />

        {/* Close */}
        <button
          onClick={onClose}
          data-testid="withdraw-received-close"
          className="absolute top-4 right-4 z-10 h-8 w-8 rounded-full grid place-items-center text-white/50 hover:text-white hover:bg-white/10 transition"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className="relative px-7 pt-9 pb-7 text-center">
          {/* Animated check */}
          <div className="mx-auto relative mb-5" style={{ width: 96, height: 96 }}>
            <div
              aria-hidden
              className="absolute inset-0 rounded-full"
              style={{
                background: "radial-gradient(circle, rgba(16,185,129,0.55) 0%, rgba(16,185,129,0) 70%)",
                animation: "wr-pulse 1.6s ease-out infinite",
              }}
            />
            <div
              className="relative h-24 w-24 rounded-full grid place-items-center mx-auto"
              style={{
                background: "linear-gradient(135deg, #34d399 0%, #059669 100%)",
                boxShadow: "0 18px 48px -10px rgba(16,185,129,0.7), inset 0 -4px 12px rgba(0,0,0,0.2)",
              }}
            >
              <ArrowDownToLine size={50} strokeWidth={2.6} className="text-[#06140b]" style={{ animation: "wr-check-pop 480ms 120ms cubic-bezier(0.34,1.56,0.64,1) both" }} />
            </div>
          </div>

          {/* Title */}
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[2.4px] text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 rounded-full px-3 py-1 mb-3">
            <PartyPopper size={11} /> Cash Received
          </div>
          <h2 className="text-[26px] font-black tracking-tight leading-tight">
            Withdraw Received{" "}
            <span className="bg-gradient-to-r from-emerald-300 to-amber-300 bg-clip-text text-transparent">Successful</span>
          </h2>
          <p className="text-[13px] text-white/55 mt-1.5">
            The user's withdrawal has been verified — funds are now back in your wallet.
          </p>

          {/* Amount big */}
          <div
            className="mt-6 rounded-2xl border border-emerald-500/25 px-5 py-4"
            style={{ background: "linear-gradient(180deg, rgba(16,185,129,0.10) 0%, rgba(8,14,22,0.4) 100%)" }}
          >
            <div className="text-[10.5px] uppercase tracking-[1.8px] text-emerald-300/80 font-bold">Total Credited</div>
            <div
              data-testid="withdraw-received-amount"
              className="font-mono font-black text-emerald-300 mt-1"
              style={{ fontSize: 38, lineHeight: 1, textShadow: "0 0 28px rgba(16,185,129,0.4)" }}
            >
              {fmt(totalCredited)}
            </div>
            <div className="text-[12px] text-white/65 mt-2">
              from <span className="font-mono font-bold text-amber-300" data-testid="withdraw-received-username">@{username}</span>
              {playerId ? <span className="text-white/35"> · #{playerId}</span> : null}
            </div>
            {commission > 0 && (
              <div className="mt-3 flex items-center justify-center gap-2 text-[11px]">
                <span className="text-white/45">Withdraw amount:</span>
                <span className="font-mono font-bold text-white/85">{fmt(amount)}</span>
                <span className="text-white/25">+</span>
                <span className="text-emerald-300/80">commission {commissionPct}%</span>
                <span className="font-mono font-bold text-emerald-300">{fmt(commission)}</span>
              </div>
            )}
          </div>

          {/* Meta grid */}
          <div className="mt-4 grid grid-cols-2 gap-2.5 text-left">
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2.5">
              <div className="text-[9.5px] uppercase tracking-[1.5px] text-white/40 font-bold flex items-center gap-1">
                <Wallet size={10} /> New Balance
              </div>
              <div className="font-mono font-bold text-emerald-300 text-[14px] mt-0.5" data-testid="withdraw-received-new-balance">
                {fmt(newBalance)}
              </div>
            </div>
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2.5">
              <div className="text-[9.5px] uppercase tracking-[1.5px] text-white/40 font-bold flex items-center gap-1">
                <Receipt size={10} /> Reference #
              </div>
              <div className="font-mono font-bold text-white text-[14px] mt-0.5">
                {refId || "—"}
              </div>
            </div>
          </div>

          <div className="text-[10.5px] text-white/40 mt-3">{when}</div>

          {/* CTA */}
          <button
            onClick={onClose}
            data-testid="withdraw-received-done"
            className="mt-6 w-full h-11 rounded-xl font-bold text-[14px] text-[#06140b] transition active:scale-[0.98]"
            style={{
              background: "linear-gradient(135deg, #34d399 0%, #10b981 50%, #059669 100%)",
              boxShadow: "0 10px 30px -8px rgba(16,185,129,0.55)",
            }}
          >
            Done
          </button>
        </div>
      </div>

      <style>{`
        @keyframes wr-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes wr-pop-in {
          0%   { opacity: 0; transform: translateY(20px) scale(0.92); }
          60%  { opacity: 1; transform: translateY(-4px) scale(1.02); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes wr-check-pop {
          0%   { transform: scale(0.3) rotate(-12deg); opacity: 0; }
          60%  { transform: scale(1.18) rotate(4deg);  opacity: 1; }
          100% { transform: scale(1) rotate(0);        opacity: 1; }
        }
        @keyframes wr-pulse {
          0%   { transform: scale(0.9); opacity: 0.85; }
          70%  { transform: scale(1.6); opacity: 0;    }
          100% { transform: scale(1.6); opacity: 0;    }
        }
        @keyframes wr-confetti {
          0%   { transform: translateY(0) rotate(0deg);    opacity: 0;   }
          10%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(540deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
